import { useCallback, useState, type ChangeEvent, type FormEvent } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { startToolOperation, trackToolRejection, type ToolAnalyticsContext, type ToolErrorCode } from '@saas-pages/shared/public/runtime'
import {
  DICE_SIDES,
  MAX_DICE_COUNT,
  MAX_DICE_MODIFIER,
  parseDiceFormula,
  rollDice,
  syncDiceSettingsFromFormula,
  type DiceError,
  type DiceRollResult,
} from '@/pages/dice-roller/dice-roller'
import { diceRollerRoute } from '@/shared/utility-registry'

const ANALYTICS_CONTEXT = {
  app: 'calcdeck',
  domain: 'calcdeck.ru',
  route: diceRollerRoute.path,
  utility: diceRollerRoute.analyticsKey,
  processingMode: 'local',
} satisfies ToolAnalyticsContext

export function DiceRollerPage() {
  const [formula, setFormula] = useState('3d6')
  const [count, setCount] = useState('3')
  const [sides, setSides] = useState<(typeof DICE_SIDES)[number]>(6)
  const [modifier, setModifier] = useState('0')
  const [result, setResult] = useState<Extract<DiceRollResult, { status: 'success' }> | null>(null)
  const [error, setError] = useState<DiceError | null>(null)

  const clearResult = useCallback(() => {
    setResult(null)
    setError(null)
  }, [])

  const updateSettingsFormula = useCallback((nextCount: string, nextSides: number, nextModifier: string) => {
    const trimmedModifier = nextModifier.trim()
    const modifierNumber = Number(trimmedModifier)
    const suffix = trimmedModifier === '' || modifierNumber === 0
      ? ''
      : /^[+-]/.test(trimmedModifier) ? trimmedModifier : `${modifierNumber > 0 ? '+' : ''}${trimmedModifier}`
    setFormula(`${nextCount}d${nextSides}${suffix}`)
    clearResult()
  }, [clearResult])

  const handleCount = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setCount(value)
    updateSettingsFormula(value, sides, modifier)
  }, [modifier, sides, updateSettingsFormula])

  const handleModifier = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setModifier(value)
    updateSettingsFormula(count, sides, value)
  }, [count, sides, updateSettingsFormula])

  const handleSides = useCallback((nextSides: (typeof DICE_SIDES)[number]) => {
    setSides(nextSides)
    updateSettingsFormula(count, nextSides, modifier)
  }, [count, modifier, updateSettingsFormula])

  const handleFormula = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const nextFormula = event.target.value
    const synced = syncDiceSettingsFromFormula(nextFormula)
    setFormula(nextFormula)
    if (synced) {
      setCount(String(synced.count))
      setSides(synced.sides)
      setModifier(String(synced.modifier))
    }
    clearResult()
  }, [clearResult])

  const roll = useCallback((event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const parsed = parseDiceFormula({ formula })
    if (parsed.status === 'error') {
      setResult(null)
      setError(parsed.error)
      trackToolRejection(ANALYTICS_CONTEXT, diceAnalyticsError(parsed.error))
      return
    }

    const startedAt = performance.now()
    const operation = startToolOperation(ANALYTICS_CONTEXT)
    const rolled = rollDice({ formula })
    if (rolled.status === 'error') {
      setResult(null)
      setError(rolled.error)
      operation.error(rolled.error === 'processing_failed' ? 'processing_failed' : diceAnalyticsError(rolled.error))
      return
    }

    const normalized = parseDiceFormula({ formula: rolled.formula })
    if (normalized.status === 'ready') {
      setCount(String(normalized.value.count))
      setSides(normalized.value.sides)
      setModifier(String(normalized.value.modifier))
      setFormula(normalized.value.normalizedFormula)
    }
    setResult(rolled)
    setError(null)
    operation.success(performance.now() - startedAt)
  }, [formula])

  return (
    <div className="mx-auto max-w-6xl">
      <section className="overflow-hidden rounded-lg border bg-card text-card-foreground shadow">
        <div aria-hidden="true" className="h-1.5 bg-gradient-to-r from-blue-700 via-indigo-500 to-sky-400" />
        <div className="grid min-w-0 lg:grid-cols-[minmax(0,1fr)_minmax(320px,0.9fr)] lg:grid-rows-[auto_1fr]">
          <form onSubmit={roll} className="min-w-0 space-y-3 p-4 sm:p-6 lg:col-start-1 lg:row-start-1 lg:border-r lg:pb-0">
            <label htmlFor="dice-formula" className="block">
              <span className="mb-1 block text-sm font-semibold">Формула броска</span>
              <input
                id="dice-formula"
                value={formula}
                maxLength={32}
                autoComplete="off"
                onChange={handleFormula}
                aria-invalid={error !== null}
                aria-describedby={error ? 'dice-error' : 'dice-formula-help'}
                className={cn(
                  'h-12 w-full rounded-md border bg-background px-4 font-mono text-lg font-semibold tabular-nums outline-none focus:border-primary focus:ring-2 focus:ring-ring/20',
                  error && 'border-destructive focus:border-destructive',
                )}
              />
              <span id="dice-formula-help" className="mt-1 block text-xs leading-5 text-muted-foreground">
                Например: 3d6, 2к20 или 3d6+2. До {MAX_DICE_COUNT} кубиков и модификатор от −{MAX_DICE_MODIFIER} до +{MAX_DICE_MODIFIER}.
              </span>
            </label>
            {error && <div id="dice-error" role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">{diceErrorText(error)}</div>}
            <Button type="submit" className="h-11 w-full sm:w-auto">Бросить кубики</Button>
          </form>

          <div className="flex min-w-0 flex-col bg-background/55 p-4 sm:p-6 lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:min-h-[360px]" aria-live="polite">
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm font-medium text-muted-foreground">Результат</span>
              <span className="rounded-md border bg-card px-2 py-1 font-mono text-xs">{result?.formula ?? 'Ожидает броска'}</span>
            </div>
            {result ? (
              <>
                <div className="mt-2 rounded-lg border border-primary/35 bg-card p-4 text-center shadow-sm sm:mt-5 sm:p-5">
                  <div className="text-sm text-muted-foreground">Итог</div>
                  <div className="mt-2 font-mono text-5xl font-bold tabular-nums">{result.total}</div>
                  {result.modifier !== 0 && <div className="mt-2 text-xs text-muted-foreground">С учётом модификатора {result.modifier > 0 ? '+' : ''}{result.modifier}</div>}
                </div>
                <div className="mt-4 max-h-64 overflow-auto rounded-md border bg-card p-4">
                  <div className="mb-3 text-sm font-semibold">Отдельные броски</div>
                  <ol className="flex flex-wrap gap-2">
                    {result.rolls.map((value, index) => <li key={index} className="rounded-md border bg-background px-3 py-2 font-mono font-semibold tabular-nums">{value}</li>)}
                  </ol>
                </div>
              </>
            ) : (
              <p className="mt-2 text-sm leading-6 text-muted-foreground">Нажмите «Бросить кубики»: итог и отдельные броски появятся здесь.</p>
            )}
          </div>

          <div className="min-w-0 space-y-4 border-t p-4 sm:p-6 lg:col-start-1 lg:row-start-2 lg:border-r lg:border-t-0">
            <div>
              <div className="mb-2 text-sm font-semibold">Тип кубика</div>
              <div className="grid grid-cols-4 gap-2 sm:grid-cols-7">
                {DICE_SIDES.map((dieSides) => (
                  <button
                    key={dieSides}
                    type="button"
                    aria-pressed={sides === dieSides}
                    onClick={() => handleSides(dieSides)}
                    className={cn(
                      'rounded-md border px-2 py-2.5 font-mono text-sm font-semibold transition-colors',
                      sides === dieSides ? 'border-primary bg-primary text-primary-foreground' : 'bg-background hover:border-primary/40',
                    )}
                  >
                    d{dieSides}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <NumberInput id="dice-count" label="Количество кубиков" value={count} onChange={handleCount} />
              <NumberInput id="dice-modifier" label="Модификатор" value={modifier} onChange={handleModifier} />
            </div>

          </div>
        </div>
      </section>

      <section className="mt-8 grid gap-4 md:grid-cols-3">
        <InfoCard title="Семь типов" text="Доступны d4, d6, d8, d10, d12, d20 и d100 на одной странице." />
        <InfoCard title="Локальный бросок" text="Результаты создаются в браузере и не отправляются на сервер." />
        <InfoCard title="Для обычных задач" text="Инструмент не предназначен для азартных или юридически значимых розыгрышей." />
      </section>
    </div>
  )
}

function NumberInput({ id, label, value, onChange }: { id: string; label: string; value: string; onChange: (event: ChangeEvent<HTMLInputElement>) => void }) {
  return <label htmlFor={id}><span className="mb-2 block text-sm font-semibold">{label}</span><input id={id} value={value} inputMode="numeric" onChange={onChange} className="h-11 w-full rounded-md border bg-background px-3 font-mono tabular-nums outline-none focus:border-primary focus:ring-2 focus:ring-ring/20" /></label>
}

function InfoCard({ title, text }: { title: string; text: string }) {
  return <Card className="rounded-lg"><CardContent className="pt-6"><div className="font-semibold">{title}</div><p className="mt-2 text-sm leading-6 text-muted-foreground">{text}</p></CardContent></Card>
}

function diceErrorText(error: DiceError): string {
  const messages: Record<DiceError, string> = {
    empty_formula: 'Введите формулу броска, например 3d6+2.',
    invalid_formula: 'Используйте формат NdX+M, например 3d6+2.',
    invalid_count: `Количество кубиков должно быть целым числом от 1 до ${MAX_DICE_COUNT}.`,
    unsupported_die: 'Поддерживаются только d4, d6, d8, d10, d12, d20 и d100.',
    invalid_modifier: `Модификатор должен быть целым числом от −${MAX_DICE_MODIFIER} до +${MAX_DICE_MODIFIER}.`,
    processing_failed: 'Не удалось получить случайные данные в браузере. Проверьте настройки браузера и попробуйте ещё раз.',
  }
  return messages[error]
}

function diceAnalyticsError(error: Exclude<DiceError, 'processing_failed'>): ToolErrorCode {
  if (error === 'unsupported_die') return 'unsupported_input'
  if (error === 'invalid_count' || error === 'invalid_modifier') return 'size_limit'
  return 'invalid_input'
}
